import React, { createContext, useContext, useRef, useCallback } from 'react';

/**
 * SwipeAction 列表上下文
 *
 * 用于在 FlatList/ScrollView 列表中，让 DbSwipeAction 组件能够
 * 在水平滑动时自动禁用父级列表的滚动，从而解决手势冲突问题。
 * 同时支持多个 SwipeAction 之间的互斥（打开一个时自动关闭其他）。
 */
export interface SwipeListContextType {
    /** 控制父级列表的滚动启用/禁用 */
    setScrollEnabled: (enabled: boolean) => void;
    /** 注册一个 SwipeAction 的关闭函数 */
    registerSwipeable: (id: string, closeFn: () => void) => void;
    /** 注销一个 SwipeAction */
    unregisterSwipeable: (id: string) => void;
    /** 打开某个 SwipeAction 时，关闭其他所有 */
    closeOthers: (currentId: string) => void;
}

export const SwipeListContext = createContext<SwipeListContextType | null>(null);

/**
 * 使用 SwipeList 上下文
 */
export function useSwipeListContext() {
    return useContext(SwipeListContext);
}

/**
 * SwipeListProvider - 包裹 FlatList/ScrollView 以支持内部 DbSwipeAction 的手势协调
 *
 * @example
 * ```tsx
 * const listRef = useRef<FlatList>(null);
 *
 * <SwipeListProvider listRef={listRef}>
 *   <FlatList ref={listRef} ... />
 * </SwipeListProvider>
 * ```
 */
export function SwipeListProvider({
    children,
    listRef,
}: {
    children: React.ReactNode;
    listRef: React.RefObject<any>;
}) {
    // 存储所有注册的 SwipeAction 关闭函数
    const swipeablesRef = useRef<Map<string, () => void>>(new Map());

    const contextValue = React.useMemo<SwipeListContextType>(() => ({
        setScrollEnabled: (enabled: boolean) => {
            const list = listRef.current;
            if (!list) return;

            // 兼容 FlatList、ScrollView 等多种列表组件
            if (list.setNativeProps) {
                list.setNativeProps({ scrollEnabled: enabled });
            } else if (list.getScrollResponder) {
                const scrollResponder = list.getScrollResponder();
                scrollResponder?.setNativeProps?.({ scrollEnabled: enabled });
            }
        },
        registerSwipeable: (id: string, closeFn: () => void) => {
            swipeablesRef.current.set(id, closeFn);
        },
        unregisterSwipeable: (id: string) => {
            swipeablesRef.current.delete(id);
        },
        closeOthers: (currentId: string) => {
            // 关闭除当前id以外的所有SwipeAction
            swipeablesRef.current.forEach((closeFn, id) => {
                if (id !== currentId) {
                    closeFn();
                }
            });
        },
    }), [listRef]);

    return (
        <SwipeListContext.Provider value={contextValue}>
            {children}
        </SwipeListContext.Provider>
    );
}
